/**
 * 塊 3 — 内訳（Screen Contract「塊 3」・State Matrix D / C / K 群・Experience Spec U-14〜U-21 / U-29）。
 *
 * 列の区分は常に `顧客` / `自社`（合算の列を作らない・AC-3）。顧客の収集開始前は顧客の列が `収集開始前`（C1 / AC-4）。
 * 比較（K 群）では各区分の列が A / B / 差 になる。差は符号つきの件数で、色も % も付けない（K8）。
 * 表示するのは識別子・route パターン・機能名・件数・人数・日付だけ（守るライン #1）。
 */
import { Fragment, type ReactNode, useState } from 'react';
import type {
  FeatureTableRow,
  FeatureUsageConfigured,
  FeatureUsageTables,
  PairCount,
  UsageTab,
} from '../../lib/feature-usage-api';
import { CopyButton } from './CopyButton';
import { formatDiff, formatDuration, formatInt, lengthOf, shortDay, workspaceName } from './format';

export type SortKey = 'clicks' | 'views' | 'lastDay';
export type SortDir = 'asc' | 'desc';
export interface SortState {
  key: SortKey | null;
  dir: SortDir;
}

/** 並べ替え（D11）。見出しの件数 / 最終利用日で降順 ⇄ 昇順。値が同じなら元の順を保つ。 */
function sortBy<T>(rows: readonly T[], value: (r: T) => number | string, dir: SortDir): T[] {
  const sign = dir === 'asc' ? 1 : -1;
  return rows
    .map((r, i) => ({ r, i }))
    .sort((x, y) => {
      const a = value(x.r);
      const b = value(y.r);
      return a === b ? x.i - y.i : a > b ? sign : -sign;
    })
    .map(({ r }) => r);
}

function SortButton({
  label,
  k,
  sort,
  onSort,
}: {
  label: string;
  k: SortKey;
  sort: SortState;
  onSort: (k: SortKey) => void;
}) {
  const active = sort.key === k;
  return (
    <button
      type="button"
      onClick={() => onSort(k)}
      aria-pressed={active}
      className={active ? 'text-slate-900' : undefined}
    >
      {label}
    </button>
  );
}

const NOT_STARTED = '収集開始前';
const NONE = 'その後の操作なし';

interface Ctx {
  data: FeatureUsageConfigured;
  tables: FeatureUsageTables;
  previous: FeatureUsageTables | null;
  /** 比較の 1 日あたり（長さが違うとき）。 */
  perDay: { a: number; b: number } | null;
}

const customerOff = (t: FeatureUsageTables) => t.collection.customer === 'not_started';

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return (
    <th className={`px-3 py-2 text-left text-xs font-medium text-slate-500 ${className}`}>
      {children}
    </th>
  );
}
function Td({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <td className={`px-3 py-2 align-top text-sm tabular-nums ${className}`}>{children}</td>;
}

function Badge({ children }: { children: ReactNode }) {
  return (
    <span className="mr-2 rounded border border-slate-300 px-1.5 py-0.5 text-xs text-slate-600">
      {children}
    </span>
  );
}

/** 顧客のセル: 収集開始前なら値の代わりに `収集開始前`（0 や空欄にしない）。 */
function CustomerCell({ t, children }: { t: FeatureUsageTables; children: ReactNode }) {
  return customerOff(t) ? <span className="text-amber-700">{NOT_STARTED}</span> : <>{children}</>;
}
/** 自社のセル: 自社一覧が空なら `—`（C6）。 */
function AlysCell({ t, children }: { t: FeatureUsageTables; children: ReactNode }) {
  return t.meta.alysDefined ? <>{children}</> : <>—</>;
}

/** 区分の見出し（上段）。開始日をまたぐ期間は `{開始日} 以降の分` を添える（C4）。 */
function SideHeader({
  t,
  side,
  colSpan,
}: {
  t: FeatureUsageTables;
  side: 'customer' | 'alys';
  colSpan: number;
}) {
  return (
    <th
      colSpan={colSpan}
      className="border-b border-slate-200 px-3 pt-2 text-left text-xs font-semibold text-slate-700"
    >
      {side === 'customer' ? '顧客' : '自社'}
      {side === 'customer' && t.collection.sinceDay && (
        <span className="ml-2 font-normal text-slate-500">{t.collection.sinceDay} 以降の分</span>
      )}
    </th>
  );
}

const lastDayCell = (d: string | null) => (d ? shortDay(d) : '期間内なし');
const later = (a: string | null, b: string | null) =>
  a === null ? b : b === null || a > b ? a : b;

// ---------------------------------------------------------------------------
// 比較の値
// ---------------------------------------------------------------------------

function compareValues(ctx: Ctx, a: number, b: number) {
  if (!ctx.perDay) return { a: formatInt(a), b: formatInt(b), diff: formatDiff(b - a) };
  const pa = a / ctx.perDay.a;
  const pb = b / ctx.perDay.b;
  return { a: pa.toFixed(1), b: pb.toFixed(1), diff: formatDiff(pb - pa, 1) };
}

function CompareCells({
  ctx,
  side,
  a,
  b,
}: {
  ctx: Ctx;
  side: 'customer' | 'alys';
  a: number;
  b: number;
}) {
  const prev = ctx.previous as FeatureUsageTables;
  if (side === 'customer' && (customerOff(prev) || customerOff(ctx.tables))) {
    // 収集開始前と数字の差は取れない（K5）
    return (
      <>
        <Td>
          {customerOff(prev) ? <span className="text-amber-700">{NOT_STARTED}</span> : formatInt(a)}
        </Td>
        <Td>
          {customerOff(ctx.tables) ? (
            <span className="text-amber-700">{NOT_STARTED}</span>
          ) : (
            formatInt(b)
          )}
        </Td>
        <Td>—</Td>
      </>
    );
  }
  if (side === 'alys' && !ctx.tables.meta.alysDefined) {
    return (
      <>
        <Td>—</Td>
        <Td>—</Td>
        <Td>—</Td>
      </>
    );
  }
  const v = compareValues(ctx, a, b);
  return (
    <>
      <Td>{v.a}</Td>
      <Td>{v.b}</Td>
      <Td className="text-slate-700">{v.diff}</Td>
    </>
  );
}

function CompareHeader({ label }: { label: string }) {
  return (
    <>
      <Th>{label} A</Th>
      <Th>{label} B</Th>
      <Th>差</Th>
    </>
  );
}

function OnlyInOne({ inA, inB }: { inA: boolean; inB: boolean }) {
  if (inA === inB) return null;
  return (
    <p className="mt-1 text-xs text-slate-500">
      期間 {inA ? 'A' : 'B'} にだけあります（識別子が変わった可能性があります）
    </p>
  );
}

// ---------------------------------------------------------------------------
// 機能別
// ---------------------------------------------------------------------------

function FeatureLabel({
  row,
  open,
  onToggle,
}: {
  row: FeatureTableRow;
  open: boolean;
  onToggle: () => void;
}) {
  if (row.kind === 'unclassified') {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Badge>未分類</Badge>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="font-mono text-sm text-slate-900"
        >
          {row.label}
        </button>
        {row.identifier && <CopyButton label="識別子をコピー" text={row.identifier} />}
      </div>
    );
  }
  if (row.kind === 'noIdentifier') {
    return (
      <div>
        <Badge>識別子なし</Badge>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="text-sm text-slate-900"
        >
          {row.label}
        </button>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="text-left text-sm font-medium text-slate-900"
    >
      {row.label}
    </button>
  );
}

function FeatureDetail({
  row,
  colSpan,
  onSelectWorkspace,
}: {
  row: FeatureTableRow;
  colSpan: number;
  onSelectWorkspace: (id: string) => void;
}) {
  return (
    <tr data-testid={`feature-detail-${row.key}`} className="bg-slate-50">
      <td colSpan={colSpan} className="px-6 py-3 text-xs text-slate-700">
        {row.workspaces.length > 0 && (
          <table className="mb-3 w-auto">
            <thead>
              <tr>
                <Th>workspace</Th>
                <Th>区分</Th>
                <Th>最終利用日</Th>
                <Th>利用日数</Th>
                <Th>途中でやめた</Th>
                <Th>エラー</Th>
              </tr>
            </thead>
            <tbody>
              {row.workspaces.map((w) => (
                <tr key={w.workspaceId}>
                  <Td>
                    <button
                      type="button"
                      onClick={() => onSelectWorkspace(w.workspaceId)}
                      className="underline"
                    >
                      {workspaceName(w)}
                    </button>
                  </Td>
                  <Td>{w.side === 'customer' ? '顧客' : '自社'}</Td>
                  <Td>{lastDayCell(w.lastDay)}</Td>
                  <Td>{w.days} 日</Td>
                  <Td>{w.abandoned === null ? '—' : formatInt(w.abandoned)}</Td>
                  <Td>
                    {Object.keys(w.errors).length === 0
                      ? '—'
                      : Object.entries(w.errors).map(([code, n]) => (
                          <span key={code} className="mr-2 font-mono">
                            {code}: {n}
                          </span>
                        ))}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {row.parts.length > 0 && (
          <table className="w-auto">
            <thead>
              <tr>
                <Th>画面</Th>
                <Th>要素</Th>
                <Th>顧客 クリック</Th>
                <Th>自社 クリック</Th>
              </tr>
            </thead>
            <tbody>
              {row.parts.map((p) => (
                <tr key={`${p.routePattern}/${p.elementKey}/${p.elementKind}`}>
                  <Td className="font-mono">{p.routePattern}</Td>
                  <Td className="font-mono">
                    {p.elementKey ?? `識別子なし（${p.elementKind ?? 'other'}）`}
                  </Td>
                  <Td>{formatInt(p.customer)}</Td>
                  <Td>{formatInt(p.alys)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </td>
    </tr>
  );
}

function FeaturesTable({
  ctx,
  sort,
  onSort,
  onSelectWorkspace,
}: {
  ctx: Ctx;
  sort: SortState;
  onSort: (k: SortKey) => void;
  onSelectWorkspace: (id: string) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const t = ctx.tables;
  const compare = ctx.previous !== null;
  const prevByKey = new Map((ctx.previous?.features ?? []).map((r) => [r.key, r]));

  let rows = t.features;
  if (!compare && (sort.key === 'clicks' || sort.key === 'lastDay')) {
    // 未分類・識別子なしは並べ替えに関わらず最下部（D11）。
    const features = rows.filter((r) => r.kind === 'feature');
    const rest = rows.filter((r) => r.kind !== 'feature');
    const key = sort.key;
    rows = sortBy(
      features,
      (r) =>
        key === 'clicks'
          ? r.customer.clicks + r.alys.clicks
          : (later(r.customer.lastDay, r.alys.lastDay) ?? ''),
      sort.dir,
    ).concat(rest);
  }
  if (compare) {
    const keys = new Set(rows.map((r) => r.key));
    rows = rows.concat((ctx.previous?.features ?? []).filter((r) => !keys.has(r.key)));
  }
  const cols = compare ? 8 : 11;
  const hasUnclassified = t.features.some((r) => r.kind === 'unclassified');
  const active = (r: FeatureTableRow | undefined) =>
    !!r && r.customer.clicks + r.customer.views + r.alys.clicks + r.alys.views > 0;

  return (
    <>
      <table data-testid="feature-usage-features" className="w-full">
        <thead>
          <tr>
            <th />
            <SideHeader t={t} side="customer" colSpan={compare ? 3 : 5} />
            <SideHeader t={t} side="alys" colSpan={compare ? 3 : 2} />
            {!compare && <th colSpan={2} />}
          </tr>
          <tr className="border-b border-slate-200">
            <Th>機能</Th>
            {compare ? (
              <>
                <CompareHeader label="クリック" />
                <CompareHeader label="クリック" />
              </>
            ) : (
              <>
                <Th>利用 workspace</Th>
                <Th>利用者</Th>
                <Th>利用日数</Th>
                <Th>
                  <SortButton label="クリック" k="clicks" sort={sort} onSort={onSort} />
                </Th>
                <Th>
                  <SortButton label="最終利用日" k="lastDay" sort={sort} onSort={onSort} />
                </Th>
                <Th>クリック</Th>
                <Th>最終利用日</Th>
                <Th>
                  <span title="流れを定義した機能だけ">途中でやめた</span>
                </Th>
                <Th>エラー（顧客 / 自社）</Th>
              </>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row) => {
            const isOpen = open === row.key;
            const prev = prevByKey.get(row.key);
            const inB = t.features.some((r) => r.key === row.key && active(r));
            return (
              <Fragment key={row.key}>
                <tr data-testid={`feature-row-${row.key}`}>
                  <Td>
                    <FeatureLabel
                      row={row}
                      open={isOpen}
                      onToggle={() => setOpen(isOpen ? null : row.key)}
                    />
                    {/* U-14 は対応表に無い識別子だけ。対応表にある機能は片方で使われなかっただけ（識別子は変わっていない）。 */}
                    {compare && row.kind !== 'feature' && (
                      <OnlyInOne inA={active(prev)} inB={inB} />
                    )}
                  </Td>
                  {compare ? (
                    <>
                      <CompareCells
                        ctx={ctx}
                        side="customer"
                        a={prev?.customer.clicks ?? 0}
                        b={inB ? row.customer.clicks : 0}
                      />
                      <CompareCells
                        ctx={ctx}
                        side="alys"
                        a={prev?.alys.clicks ?? 0}
                        b={inB ? row.alys.clicks : 0}
                      />
                    </>
                  ) : (
                    <>
                      <Td>
                        <CustomerCell t={t}>{formatInt(row.customer.workspaces)}</CustomerCell>
                      </Td>
                      <Td>
                        <CustomerCell t={t}>{row.customer.users} 人</CustomerCell>
                      </Td>
                      <Td>
                        <CustomerCell t={t}>{row.customer.days} 日</CustomerCell>
                      </Td>
                      <Td>
                        <CustomerCell t={t}>{formatInt(row.customer.clicks)}</CustomerCell>
                      </Td>
                      <Td>
                        <CustomerCell t={t}>{lastDayCell(row.customer.lastDay)}</CustomerCell>
                      </Td>
                      <Td>
                        <AlysCell t={t}>{formatInt(row.alys.clicks)}</AlysCell>
                      </Td>
                      <Td>
                        <AlysCell t={t}>{lastDayCell(row.alys.lastDay)}</AlysCell>
                      </Td>
                      <Td>
                        {row.abandoned === null ? (
                          '—'
                        ) : (
                          <CustomerCell t={t}>{formatInt(row.abandoned.customer)}</CustomerCell>
                        )}
                      </Td>
                      <Td>
                        <CustomerCell t={t}>{formatInt(row.customer.errors)}</CustomerCell>
                        {' / '}
                        <AlysCell t={t}>{formatInt(row.alys.errors)}</AlysCell>
                      </Td>
                    </>
                  )}
                </tr>
                {isOpen && (
                  <FeatureDetail row={row} colSpan={cols} onSelectWorkspace={onSelectWorkspace} />
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {hasUnclassified && (
        <p className="px-3 py-2 text-xs text-slate-600">
          対応表に無い識別子です。対応表に追加すると、過去の分も機能名で集計されます。
        </p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// 画面別
// ---------------------------------------------------------------------------

function ScreensTable({
  ctx,
  retentionNote,
  sort,
  onSort,
}: {
  ctx: Ctx;
  retentionNote: boolean;
  sort: SortState;
  onSort: (k: SortKey) => void;
}) {
  const t = ctx.tables;
  const compare = ctx.previous !== null;
  const prevBy = new Map((ctx.previous?.screens ?? []).map((s) => [s.routePattern, s]));
  let rows = compare
    ? [
        ...t.screens,
        ...(ctx.previous?.screens ?? []).filter(
          (s) => !t.screens.some((x) => x.routePattern === s.routePattern),
        ),
      ]
    : t.screens;
  if (!compare && (sort.key === 'views' || sort.key === 'lastDay')) {
    const key = sort.key;
    rows = sortBy(
      rows,
      (s) =>
        key === 'views'
          ? s.customer.views + s.alys.views
          : (later(s.customer.lastDay, s.alys.lastDay) ?? ''),
      sort.dir,
    );
  }
  return (
    <table data-testid="feature-usage-screens" className="w-full">
      <thead>
        <tr>
          <th />
          <SideHeader t={t} side="customer" colSpan={compare ? 3 : 4} />
          <SideHeader t={t} side="alys" colSpan={compare ? 3 : 2} />
          {!compare && <th />}
        </tr>
        <tr className="border-b border-slate-200">
          <Th>画面</Th>
          {compare ? (
            <>
              <CompareHeader label="表示" />
              <CompareHeader label="表示" />
            </>
          ) : (
            <>
              <Th>
                <SortButton label="表示" k="views" sort={sort} onSort={onSort} />
              </Th>
              <Th>利用者</Th>
              <Th>
                滞在時間（中央値）
                {retentionNote && <span className="block font-normal">生の記録が残る分だけ</span>}
              </Th>
              <Th>
                <SortButton label="最終表示日" k="lastDay" sort={sort} onSort={onSort} />
              </Th>
              <Th>表示</Th>
              <Th>最終表示日</Th>
              <Th>エラー（顧客 / 自社）</Th>
            </>
          )}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((s) => {
          const prev = prevBy.get(s.routePattern);
          const inB = t.screens.some((x) => x.routePattern === s.routePattern);
          return (
            <tr key={s.routePattern} data-testid={`screen-row-${s.routePattern}`}>
              <Td>
                <span className="font-mono">{s.routePattern}</span>
                <span className="block text-xs text-slate-500">{s.feature ?? '未分類'}</span>
                {compare && <OnlyInOne inA={!!prev} inB={inB} />}
              </Td>
              {compare ? (
                <>
                  <CompareCells
                    ctx={ctx}
                    side="customer"
                    a={prev?.customer.views ?? 0}
                    b={inB ? s.customer.views : 0}
                  />
                  <CompareCells
                    ctx={ctx}
                    side="alys"
                    a={prev?.alys.views ?? 0}
                    b={inB ? s.alys.views : 0}
                  />
                </>
              ) : (
                <>
                  <Td>
                    <CustomerCell t={t}>{formatInt(s.customer.views)}</CustomerCell>
                  </Td>
                  <Td>
                    <CustomerCell t={t}>{s.customer.users} 人</CustomerCell>
                  </Td>
                  <Td>
                    <CustomerCell t={t}>
                      {s.customer.dwellMedianMs === null
                        ? '—'
                        : formatDuration(s.customer.dwellMedianMs)}
                    </CustomerCell>
                  </Td>
                  <Td>
                    <CustomerCell t={t}>{lastDayCell(s.customer.lastDay)}</CustomerCell>
                  </Td>
                  <Td>
                    <AlysCell t={t}>{formatInt(s.alys.views)}</AlysCell>
                  </Td>
                  <Td>
                    <AlysCell t={t}>{lastDayCell(s.alys.lastDay)}</AlysCell>
                  </Td>
                  <Td>
                    <CustomerCell t={t}>{formatInt(s.customer.errors)}</CustomerCell>
                    {' / '}
                    <AlysCell t={t}>{formatInt(s.alys.errors)}</AlysCell>
                  </Td>
                </>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// 遷移の流れ（表のみ・グラフにしない）
// ---------------------------------------------------------------------------

function PairTable({
  title,
  ctx,
  pairs,
  previous,
}: {
  title: string;
  ctx: Ctx;
  pairs: PairCount[];
  previous: PairCount[] | null;
}) {
  const t = ctx.tables;
  const keyOf = (p: PairCount) => p.routePattern ?? '\u0000';
  const prevBy = new Map((previous ?? []).map((p) => [keyOf(p), p]));
  const rows = previous
    ? [...pairs, ...previous.filter((p) => !pairs.some((x) => keyOf(x) === keyOf(p)))]
    : pairs;
  return (
    <div className="min-w-0 flex-1">
      <h3 className="mb-2 text-sm font-medium text-slate-700">{title}</h3>
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-200">
            <Th>画面</Th>
            {previous ? (
              <>
                <CompareHeader label="顧客" />
                <CompareHeader label="自社" />
              </>
            ) : (
              <>
                <Th>顧客</Th>
                <Th>自社</Th>
              </>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.length === 0 && (
            <tr>
              <Td className="text-slate-500">—</Td>
            </tr>
          )}
          {rows.map((p) => {
            const prev = prevBy.get(keyOf(p));
            const inB = pairs.some((x) => keyOf(x) === keyOf(p));
            return (
              <tr key={keyOf(p)}>
                <Td className={p.routePattern ? 'font-mono' : 'text-slate-600'}>
                  {p.routePattern ?? NONE}
                </Td>
                {previous ? (
                  <>
                    <CompareCells
                      ctx={ctx}
                      side="customer"
                      a={prev?.customer ?? 0}
                      b={inB ? p.customer : 0}
                    />
                    <CompareCells ctx={ctx} side="alys" a={prev?.alys ?? 0} b={inB ? p.alys : 0} />
                  </>
                ) : (
                  <>
                    <Td>
                      <CustomerCell t={t}>{formatInt(p.customer)}</CustomerCell>
                    </Td>
                    <Td>
                      <AlysCell t={t}>{formatInt(p.alys)}</AlysCell>
                    </Td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TransitionsView({
  ctx,
  origin,
  onOrigin,
}: {
  ctx: Ctx;
  origin: string | null;
  onOrigin: (o: string) => void;
}) {
  const list = ctx.tables.transitions;
  const current = list.find((x) => x.origin === origin) ?? list[0];
  if (!current) return <p className="p-3 text-sm text-slate-600">この期間の記録はありません。</p>;
  const prev = ctx.previous?.transitions.find((x) => x.origin === current.origin) ?? null;
  return (
    <div data-testid="feature-usage-transitions" className="p-3">
      <label className="mb-3 flex items-center gap-2 text-sm text-slate-700">
        起点の画面
        <select
          value={current.origin}
          onChange={(e) => onOrigin(e.target.value)}
          className="rounded border border-slate-300 bg-white px-2 py-1 font-mono text-sm"
        >
          {list.map((x) => (
            <option key={x.origin} value={x.origin}>
              {x.origin}
            </option>
          ))}
        </select>
      </label>
      <div className="flex gap-6">
        <PairTable
          title="来た元"
          ctx={ctx}
          pairs={current.sources}
          previous={ctx.previous ? (prev?.sources ?? []) : null}
        />
        <PairTable
          title="行き先"
          ctx={ctx}
          pairs={current.destinations}
          previous={ctx.previous ? (prev?.destinations ?? []) : null}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 途中でやめた（件数の組だけ・割り算した値は出さない）
// ---------------------------------------------------------------------------

const triple = (x: { started: number; completed: number; abandoned: number }) =>
  `${x.started} / ${x.completed} / ${x.abandoned}`;

function AbandonedView({ ctx, onShowMapping }: { ctx: Ctx; onShowMapping: () => void }) {
  const t = ctx.tables;
  if (t.flows.length === 0) {
    return (
      <p className="p-3 text-sm text-slate-600">
        対応表で流れが定義されていません。
        <button type="button" onClick={onShowMapping} className="ml-2 underline">
          対応表を見る
        </button>
      </p>
    );
  }
  const compare = ctx.previous !== null;
  return (
    <div data-testid="feature-usage-abandoned">
      <p className="px-3 pt-3 text-xs text-slate-600">
        対応表で「始まり」と「完了」を決めた流れだけを数えています。
      </p>
      <table className="w-full">
        <thead>
          <tr>
            <th />
            <SideHeader t={t} side="customer" colSpan={compare ? 3 : 2} />
            <SideHeader t={t} side="alys" colSpan={compare ? 3 : 1} />
          </tr>
          <tr className="border-b border-slate-200">
            <Th>流れ</Th>
            {compare ? (
              <>
                <CompareHeader label="やめた" />
                <CompareHeader label="やめた" />
              </>
            ) : (
              <>
                <Th>開始 / 完了 / やめた</Th>
                <Th>やめた後の行き先</Th>
                <Th>開始 / 完了 / やめた</Th>
              </>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {t.flows.map((f) => {
            const prev = ctx.previous?.flows.find((x) => x.key === f.key);
            return (
              <tr key={f.key}>
                <Td>
                  {f.label}
                  {f.feature && <span className="block text-xs text-slate-500">{f.feature}</span>}
                </Td>
                {compare ? (
                  <>
                    <CompareCells
                      ctx={ctx}
                      side="customer"
                      a={prev?.customer.abandoned ?? 0}
                      b={f.customer.abandoned}
                    />
                    <CompareCells
                      ctx={ctx}
                      side="alys"
                      a={prev?.alys.abandoned ?? 0}
                      b={f.alys.abandoned}
                    />
                  </>
                ) : (
                  <>
                    <Td>
                      <CustomerCell t={t}>{triple(f.customer)}</CustomerCell>
                    </Td>
                    <Td>
                      <CustomerCell t={t}>
                        {f.destinations.filter((d) => d.customer > 0).length === 0
                          ? '—'
                          : f.destinations
                              .filter((d) => d.customer > 0)
                              .map((d) => (
                                <span key={d.routePattern ?? NONE} className="block">
                                  {`${d.routePattern ?? NONE} ${d.customer}`}
                                </span>
                              ))}
                      </CustomerCell>
                    </Td>
                    <Td>
                      <AlysCell t={t}>{triple(f.alys)}</AlysCell>
                    </Td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 使われていない
// ---------------------------------------------------------------------------

function UnusedView({ ctx }: { ctx: Ctx }) {
  const t = ctx.tables;
  return (
    <div data-testid="feature-usage-unused">
      <p className="px-3 pt-3 text-xs text-slate-600">
        対応表に載っているもの、または過去 90
        日に記録があったものだけを並べています。一度も記録がなく対応表にも無い要素は、ここには出ません。
      </p>
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-200">
            <Th>名前</Th>
            <Th>種類</Th>
            <Th>顧客の最終利用日</Th>
            <Th>自社の最終利用日</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {t.unused.map((u) => (
            <tr key={u.key}>
              <Td className={u.kind === 'recorded' ? 'font-mono' : undefined}>{u.label}</Td>
              <Td>{u.kind === 'mapped' ? '対応表にある' : '過去 90 日に記録あり'}</Td>
              <Td>
                <CustomerCell t={t}>{u.customerLastDay ?? '記録なし'}</CustomerCell>
              </Td>
              <Td>
                <AlysCell t={t}>{u.alysLastDay ?? '記録なし'}</AlysCell>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function UsageTables({
  data,
  tab,
  sort,
  origin,
  onSort,
  onOrigin,
  onSelectWorkspace,
  onShowMapping,
}: {
  data: FeatureUsageConfigured;
  tab: UsageTab;
  sort: SortState;
  origin: string | null;
  onSort: (k: SortKey) => void;
  onOrigin: (o: string) => void;
  onSelectWorkspace: (id: string) => void;
  onShowMapping: () => void;
}) {
  const ctx: Ctx = {
    data,
    tables: data.tables,
    previous: data.compare ? data.previousTables : null,
    perDay:
      data.compare && !data.compare.sameLength
        ? { a: lengthOf(data.compare.a), b: lengthOf(data.compare.b) }
        : null,
  };
  const empty = data.tables.meta.total === 0 && !ctx.previous;
  const retentionNote = data.range.fromDay < data.rawRetentionFrom;

  return (
    <div>
      {empty && <p className="px-3 pt-3 text-sm text-slate-600">この期間の記録はありません。</p>}
      {tab === 'features' && (
        <FeaturesTable
          ctx={ctx}
          sort={sort}
          onSort={onSort}
          onSelectWorkspace={onSelectWorkspace}
        />
      )}
      {!empty && tab === 'screens' && (
        <ScreensTable ctx={ctx} retentionNote={retentionNote} sort={sort} onSort={onSort} />
      )}
      {!empty && tab === 'transitions' && (
        <TransitionsView ctx={ctx} origin={origin} onOrigin={onOrigin} />
      )}
      {!empty && tab === 'abandoned' && <AbandonedView ctx={ctx} onShowMapping={onShowMapping} />}
      {!empty && tab === 'unused' && <UnusedView ctx={ctx} />}
    </div>
  );
}
